import React, { useCallback, useEffect, useState } from 'react';
import { adminAuthHeaders } from './adminCheckoutUtils';
import { exportGiftFinderCsv } from './adminExportUtils';

const FUNNEL_STEPS = ['recipient', 'collection', 'group', 'budget', 'results'];

function FunnelTable({ funnel }) {
  if (!funnel || typeof funnel !== 'object') return null;
  return (
    <div className="table-responsive">
      <table className="table table-sm table-bordered mb-0">
        <thead className="table-light">
          <tr>
            <th>Step</th>
            <th className="text-end">Views</th>
            <th className="text-end">Selections</th>
          </tr>
        </thead>
        <tbody>
          {FUNNEL_STEPS.map((step) => {
            const row = funnel[step] || { views: 0, selects: 0 };
            return (
              <tr key={step}>
                <td className="text-capitalize">{step}</td>
                <td className="text-end">{row.views ?? 0}</td>
                <td className="text-end">{row.selects ?? 0}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function rangeLabelFromQuery(rangeQuery) {
  if (!rangeQuery) return '';
  if (rangeQuery.mode === 'custom') {
    return `${rangeQuery.from || ''} – ${rangeQuery.to || ''}`;
  }
  return `Last ${rangeQuery.days || 30} days`;
}

export default function AdminGiftFinderAnalytics({ sessionToken, rangeQuery }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const p = new URLSearchParams();
      if (rangeQuery.mode === 'custom') {
        p.set('from', rangeQuery.from);
        p.set('to', rangeQuery.to);
      } else {
        p.set('days', String(rangeQuery.days));
      }
      const r = await fetch(`/api/gift_finder_analytics.php?${p}`, {
        headers: adminAuthHeaders(sessionToken),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body.success) {
        setError(body.error || 'Could not load gift finder analytics.');
        setData(null);
        return;
      }
      setData(body.analytics || null);
    } catch {
      setError('Could not load gift finder analytics.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [sessionToken, rangeQuery]);

  useEffect(() => {
    if (sessionToken) load();
  }, [sessionToken, load]);

  if (loading) {
    return <p className="small text-muted mb-0">Loading gift finder analytics…</p>;
  }
  if (error) {
    return <div className="alert alert-warning py-2 small mb-0">{error}</div>;
  }
  if (!data?.available) {
    return (
      <p className="small text-muted mb-0">
        {data?.message || 'Gift finder analytics not available — run database migration.'}
      </p>
    );
  }

  const results = data.results || {};
  const topClicks = Array.isArray(data.top_product_clicks) ? data.top_product_clicks : [];
  const topFilters = Array.isArray(data.top_filter_combos) ? data.top_filter_combos : [];

  return (
    <div className="admin-gift-finder-analytics">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
            <p className="small text-muted mb-0">
              Anonymous usage on <strong>/gift-finder</strong> — sessions, completion rate, and popular picks.
            </p>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => exportGiftFinderCsv(data, rangeLabelFromQuery(rangeQuery))}
              title="Opens in Excel"
            >
              Export CSV
            </button>
          </div>
          <div className="row g-3 mb-3">
        <div className="col-6 col-md-3">
          <div className="admin-analytics-stat">
            <div className="admin-analytics-stat__label">Sessions</div>
            <div className="admin-analytics-stat__value">{data.sessions ?? 0}</div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="admin-analytics-stat">
            <div className="admin-analytics-stat__label">Reached results</div>
            <div className="admin-analytics-stat__value">{data.completed_sessions ?? 0}</div>
            <div className="admin-analytics-stat__sub">{data.completion_rate_pct ?? 0}% completion</div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="admin-analytics-stat">
            <div className="admin-analytics-stat__label">Result views</div>
            <div className="admin-analytics-stat__value">{results.views ?? 0}</div>
            <div className="admin-analytics-stat__sub">Avg {results.avg_result_count ?? 0} products</div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="admin-analytics-stat admin-analytics-stat--muted">
            <div className="admin-analytics-stat__label">Empty results</div>
            <div className="admin-analytics-stat__value">{results.zero_result_views ?? 0}</div>
          </div>
        </div>
      </div>
      <div className="row g-3">
        <div className="col-lg-6">
          <h4 className="small fw-semibold mb-2">Funnel by step</h4>
          <FunnelTable funnel={data.funnel} />
        </div>
        <div className="col-lg-6">
          <h4 className="small fw-semibold mb-2">Top product clicks from results</h4>
          {topClicks.length === 0 ? (
            <p className="small text-muted mb-0">No product clicks recorded yet.</p>
          ) : (
            <ul className="list-group list-group-flush small">
              {topClicks.map((row) => (
                <li key={row.product_id} className="list-group-item d-flex justify-content-between px-0">
                  <code>{row.product_id}</code>
                  <span>{row.clicks}</span>
                </li>
              ))}
            </ul>
          )}
          <h4 className="small fw-semibold mb-2 mt-3">Popular filter combos</h4>
          {topFilters.length === 0 ? (
            <p className="small text-muted mb-0">No results views yet.</p>
          ) : (
            <ul className="list-group list-group-flush small">
              {topFilters.map((row, i) => (
                <li key={`${row.category}-${row.group}-${row.budget}-${i}`} className="list-group-item px-0">
                  {[row.category, row.group, row.budget].filter((x) => x && x !== 'any').join(' · ') || 'All'}
                  <span className="float-end">{row.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
          </div>
    </div>
  );
}
